import React, { createContext, useContext, useState, useRef } from 'react';
import Toast from '../components/common/Toast';

const NotificationContext = createContext(null);

export const useNotification = () => {
  const context = useContext(NotificationContext);
  if (!context) {
    throw new Error('useNotification must be used within a NotificationProvider');
  }
  return context;
};

export const NotificationProvider = ({ children }) => {
  const [notifications, setNotifications] = useState([]);
  const [toasts, setToasts] = useState([]);
  const nextId = useRef(1);
  
  const addNotification = (notification) => {
    const id = nextId.current++;
    const newNotification = {
      id,
      read: false,
      time: 'Just now',
      ...notification
    };
    
    setNotifications(prev => [newNotification, ...prev]);
    
    // Also show as toast if required
    if (notification.showToast) {
      addToast({
        id,
        message: notification.message || notification.title,
        type: mapNotificationTypeToToastType(notification.type)
      });
    }
    
    return id;
  };
  
  const markAsRead = (id) => {
    setNotifications(prev => 
      prev.map(n => n.id === id ? { ...n, read: true } : n)
    );
  };
  
  const markAllAsRead = () => {
    setNotifications(prev => 
      prev.map(n => ({ ...n, read: true }))
    );
  };
  
  const getUnreadCount = () => {
    return notifications.filter(n => !n.read).length;
  };
  
  const addToast = (toast) => {
    const id = toast.id || nextId.current++;
    setToasts(prev => [...prev, { ...toast, id }]);
    
    return id;
  };
  
  const removeToast = (id) => {
    setToasts(prev => prev.filter(t => t.id !== id));
  };
  
  const mapNotificationTypeToToastType = (type) => {
    switch (type) {
      case 'price_break':
        return 'success';
      case 'group_forming':
        return 'info';
      case 'tokens_earned':
        return 'success';
      case 'error':
        return 'error';
      default:
        return 'info';
    }
  };
  
  const value = {
    notifications,
    addNotification,
    markAsRead,
    markAllAsRead,
    getUnreadCount,
    addToast
  };
  
  return (
    <NotificationContext.Provider value={value}>
      {children}
      
      {/* Render toasts */}
      {toasts.map((toast) => (
        <Toast
          key={toast.id}
          message={toast.message}
          type={toast.type}
          duration={toast.duration || 4000}
          onClose={() => removeToast(toast.id)}
        />
      ))}
    </NotificationContext.Provider>
  );
};